import { Link } from 'react-router-dom';
import { useState } from 'react';
import { 
  Phone, Shield, Bug, Rat, Leaf, CheckCircle, Star, 
  ArrowRight, Clock, Award, Users, MapPin, ChevronDown, ChevronUp,
  Sparkles, Home as HomeIcon, Building2, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export default function Home() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const services = [
    { icon: Bug, name: 'General Pest Control', desc: 'Comprehensive protection against common household pests' },
    { icon: HomeIcon, name: 'Termite Control', desc: 'Protect your property from costly termite damage' },
    { icon: Rat, name: 'Rodent Control', desc: 'Safe and humane removal of mice and rats' },
    { icon: Shield, name: 'Bed Bug Treatment', desc: 'Complete elimination of bed bug infestations' },
    { icon: Bug, name: 'Ant Control', desc: 'Stop ant colonies before they spread' },
    { icon: Zap, name: 'Cockroach Control', desc: 'Fast-acting solutions for cockroach problems' },
    { icon: Sparkles, name: 'Mosquito Control', desc: 'Enjoy your outdoor spaces pest-free' },
    { icon: Building2, name: 'Commercial Services', desc: 'Tailored solutions for businesses of all sizes' },
  ];

  const whyChooseUs = [
    { icon: Award, title: 'Licensed Experts', desc: 'State-certified technicians with years of experience' },
    { icon: Leaf, title: 'Eco-Friendly', desc: 'Safe treatments for your family and pets' },
    { icon: Clock, title: 'Same-Day Service', desc: 'Fast response when you need it most' },
    { icon: Shield, title: '100% Guarantee', desc: 'Satisfaction guaranteed or your money back' },
  ];

  const testimonials = [
    { name: 'Sarah M.', location: 'Minneapolis, MN', text: 'Primes Pick solved our ant problem in just one visit. Professional, friendly, and thorough!', rating: 5 },
    { name: 'Robert J.', location: 'St. Paul, MN', text: 'We had a serious rodent issue. Their team was responsive and completely eliminated the problem.', rating: 5 },
    { name: 'Emily K.', location: 'Bloomington, MN', text: 'Best pest control service I have ever used. Fair pricing and amazing results!', rating: 5 },
  ];

  const faqs = [
    { q: 'How often should I schedule pest control services?', a: 'We recommend quarterly treatments for most homes to maintain a pest-free environment. However, some situations may require monthly visits, especially for severe infestations or commercial properties.' },
    { q: 'Are your treatments safe for children and pets?', a: 'Absolutely! We use eco-friendly, EPA-approved products that are safe for your family and pets. Our technicians will provide specific instructions for any precautions if needed.' },
    { q: 'What pests do you treat?', a: 'We handle all common pests including ants, cockroaches, spiders, termites, bed bugs, rodents, mosquitoes, wasps, and more. Contact us for any pest concerns!' },
    { q: 'Do you offer emergency pest control services?', a: 'Yes! We offer same-day and emergency services for urgent pest situations. Call our hotline anytime to schedule an immediate visit.' },
    { q: 'What is included in a free inspection?', a: 'Our free inspection includes a thorough assessment of your property, identification of pest types, entry points, damage assessment, and a customized treatment plan with transparent pricing.' },
  ];

  const pests = [
    { name: 'Ants', image: 'https://images.pexels.com/photos/32610024/pexels-photo-32610024.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Termites', image: 'https://images.unsplash.com/photo-1762620944311-1f9b05a51e1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHx0ZXJtaXRlJTIwZGFtYWdlJTIwd29vZCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcyNzMyMjkyfDA&ixlib=rb-4.1.0&q=85' },
    { name: 'Mosquitoes', image: 'https://images.unsplash.com/photo-1765980086761-8c6ec5858edf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxtYWNybyUyMG1vc3F1aXRvJTIwY29ja3JvYWNoJTIwbW91c2UlMjBuYXR1cmV8ZW58MHx8fHwxNzcyNzMyMjkwfDA&ixlib=rb-4.1.0&q=85' },
    { name: 'Rodents', image: 'https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=600&h=400&fit=crop' },
  ];

  const protectedHomes = [
    { image: 'https://images.pexels.com/photos/4469146/pexels-photo-4469146.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', alt: 'Beautiful family home with manicured lawn' },
    { image: 'https://images.unsplash.com/photo-1759355787118-18d16b4ea30d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxyZXNpZGVudGlhbCUyMGhvdXNlJTIwZXh0ZXJpb3IlMjBnYXJkZW4lMjBsYXdufGVufDB8fHx8MTc3MjczMzQ0N3ww&ixlib=rb-4.1.0&q=85', alt: 'Brick house with manicured garden' },
    { image: 'https://images.unsplash.com/photo-1758687126864-96b61e1b3af0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGZhbWlseSUyMGhvbWUlMjBjb3VjaCUyMGxpdmluZyUyMHJvb20lMjBzbWlsaW5nfGVufDB8fHx8MTc3MjczMzQ2M3ww&ixlib=rb-4.1.0&q=85', alt: 'Happy family in living room' },
    { image: 'https://images.unsplash.com/photo-1758687125910-c233b47f47b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMGhvbWUlMjBjb3VjaCUyMGxpdmluZyUyMHJvb20lMjBzbWlsaW5nfGVufDB8fHx8MTc3MjczMzQ2M3ww&ixlib=rb-4.1.0&q=85', alt: 'Father and son at home' },
  ];

  const cleanHomeImages = [
    { image: 'https://images.pexels.com/photos/9462220/pexels-photo-9462220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', alt: 'Clean kitchen cabinets' },
    { image: 'https://images.pexels.com/photos/9462200/pexels-photo-9462200.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', alt: 'Clean modern kitchen' },
  ];

  return (
    <div className="page-transition" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-pattern pt-20" data-testid="hero-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Leaf className="w-4 h-4" />
                Eco-Friendly Pest Solutions
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1C1A] leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Professional Pest Control Services for a{' '}
                <span className="text-gradient">Safe and Pest-Free Home</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#52525B] mb-8 leading-relaxed">
                Protect your home and business from pests with our expert pest control solutions. 
                Trusted by thousands of families across Minneapolis.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary w-full sm:w-auto" data-testid="hero-get-inspection-btn">
                    <CheckCircle className="w-5 h-5" />
                    Get Free Inspection
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-secondary w-full sm:w-auto" data-testid="hero-request-quote-btn">
                    Request a Quote
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+18883568891" className="btn-accent w-full sm:w-auto text-center" data-testid="hero-call-btn">
                  <Phone className="w-5 h-5" />
                  Call Now
                </a>
              </div>
              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-4 mt-8">
                <div className="trust-badge flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#E8F5E9]">
                  <Shield className="w-5 h-5 text-[#2E7D32]" />
                  <span className="text-sm font-medium text-[#1A1C1A]">Licensed & Insured</span>
                </div>
                <div className="trust-badge flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#E8F5E9]">
                  <Leaf className="w-5 h-5 text-[#2E7D32]" />
                  <span className="text-sm font-medium text-[#1A1C1A]">Pet & Child Safe</span>
                </div>
                <div className="trust-badge flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#E8F5E9]">
                  <Star className="w-5 h-5 text-[#F9A825]" />
                  <span className="text-sm font-medium text-[#1A1C1A]">5-Star Rated</span>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-in-right hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMGNsZWFuJTIwbW9kZXJuJTIwbGl2aW5nJTIwcm9vbSUyMGJyaWdodHxlbnwwfHx8fDE3NzI3MzIyNzZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Happy family in clean living room"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#2E7D32] rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1A1C1A]">15K+</p>
                    <p className="text-sm text-[#52525B]">Happy Customers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="intro-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Trusted Partner in Pest Control
            </h2>
            <p className="text-lg text-[#52525B] leading-relaxed">
              At Primes Pick, we have been protecting homes and businesses from unwanted pests for over a decade. 
              Our team of certified professionals uses the latest eco-friendly treatments to ensure your space 
              remains safe, healthy, and pest-free. We are committed to delivering exceptional service with 
              guaranteed results.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding" data-testid="services-section">
        <div className="container-main">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Pest Control Services
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              Comprehensive solutions for all your pest problems
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="card-service service-card-hover group"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#2E7D32] transition-colors duration-200">
                  <service.icon className="w-7 h-7 text-[#2E7D32] group-hover:text-white transition-colors duration-200" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1C1A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {service.name}
                </h3>
                <p className="text-sm text-[#52525B]">{service.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services">
              <Button className="btn-primary" data-testid="view-all-services-btn">
                View All Services
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Common Pests We Treat */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="pests-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Common Pests We Treat
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              From tiny ants to stubborn rodents, we handle them all
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pests.map((pest, index) => (
              <div key={index} className="img-zoom rounded-2xl overflow-hidden shadow-lg" data-testid={`pest-card-${index}`}>
                <img 
                  src={pest.image} 
                  alt={pest.name}
                  className="w-full h-56 object-cover"
                />
                <div className="bg-white p-4 text-center">
                  <h3 className="text-lg font-semibold text-[#1A1C1A]">{pest.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding" data-testid="why-choose-section">
        <div className="container-main">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Choose Primes Pick?
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              We are committed to providing the best pest control experience
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center p-6" data-testid={`why-card-${index}`}>
                <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1C1A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-[#52525B]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eco-Friendly Section */}
      <section className="section-padding bg-[#E8F5E9]" data-testid="eco-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Leaf className="w-4 h-4" />
                Eco-Friendly Approach
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Safe Treatments for Your Family
              </h2>
              <p className="text-lg text-[#52525B] mb-6 leading-relaxed">
                We understand that your family&apos;s safety comes first. That&apos;s why we use only EPA-approved, 
                eco-friendly products that effectively eliminate pests while being completely safe for your 
                children, pets, and the environment.
              </p>
              <ul className="space-y-3">
                {['Non-toxic formulations', 'Pet and child-safe products', 'Environmentally responsible methods', 'No harsh chemical residue'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2E7D32]" />
                    <span className="text-[#1A1C1A]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1608463123864-40a2961b7d00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwZXh0ZXJpb3IlMjBnYXJkZW4lMjBzdW5ueXxlbnwwfHx8fDE3NzI3MzIyNzh8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern home exterior"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Homes We Protect Gallery */}
      <section className="section-padding" data-testid="protected-homes-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Homes & Families We Protect
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              We keep families safe and homes pest-free across Minneapolis
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {protectedHomes.map((item, index) => (
              <div key={index} className="img-zoom rounded-2xl overflow-hidden shadow-lg" data-testid={`protected-home-${index}`}>
                <img 
                  src={item.image} 
                  alt={item.alt}
                  className="w-full h-56 object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clean & Safe Living */}
      <section className="py-16 bg-[#F7F9F7]" data-testid="clean-living-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="grid grid-cols-2 gap-4">
              {cleanHomeImages.map((item, index) => (
                <div key={index} className="img-zoom rounded-2xl overflow-hidden shadow-lg" data-testid={`clean-home-${index}`}>
                  <img 
                    src={item.image} 
                    alt={item.alt}
                    className="w-full h-48 object-cover"
                  />
                </div>
              ))}
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-white text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4" />
                Clean & Safe
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Enjoy a Cleaner, Healthier Home
              </h2>
              <p className="text-lg text-[#52525B] mb-6 leading-relaxed">
                Our professional pest control services don&apos;t just eliminate pests – they help create a cleaner, 
                healthier living environment for you and your family. Say goodbye to contamination worries and hello to peace of mind.
              </p>
              <Link to="/contact">
                <Button className="btn-primary" data-testid="clean-living-cta">
                  Get Started Today
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After */}
      <section className="section-padding" data-testid="before-after-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              See the Difference We Make
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              Real results from our satisfied customers
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <div className="bg-red-100 text-red-700 text-center py-2 font-semibold">BEFORE</div>
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop"
                alt="Pest problem before treatment"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <div className="bg-green-100 text-green-700 text-center py-2 font-semibold">AFTER</div>
              <img 
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=400&fit=crop"
                alt="Clean home after treatment"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="testimonials-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Our Customers Say
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              Trusted by thousands of homeowners across Minneapolis
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="card-testimonial testimonial-gradient bg-white"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#F9A825] fill-[#F9A825]" />
                  ))}
                </div>
                <p className="text-[#52525B] mb-4 leading-relaxed">&quot;{testimonial.text}&quot;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2E7D32] rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1C1A]">{testimonial.name}</p>
                    <p className="text-sm text-[#52525B]">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding" data-testid="service-areas-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Service Areas
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              Proudly serving Minneapolis and surrounding areas
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {['Minneapolis', 'St. Paul', 'Bloomington', 'Plymouth', 'Brooklyn Park', 'Maple Grove', 'Woodbury', 'Eden Prairie'].map((city, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#E8F5E9] px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-[#2E7D32]" />
                <span className="text-[#1A1C1A] font-medium">{city}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/service-areas">
              <Button className="btn-secondary" data-testid="view-all-areas-btn">
                View All Service Areas
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="faq-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              Got questions? We have answers!
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-border px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-semibold text-[#1A1C1A] hover:no-underline py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#52525B] pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding" data-testid="contact-form-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Get Your Free Quote Today
              </h2>
              <p className="text-lg text-[#52525B] mb-8">
                Fill out the form and one of our pest control experts will get back to you within 24 hours.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#2E7D32]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#52525B]">Call Us</p>
                    <a href="tel:+18883568891" className="font-semibold text-[#1A1C1A]">+1-888-356-8891</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#2E7D32]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#52525B]">Business Hours</p>
                    <p className="font-semibold text-[#1A1C1A]">Mon - Sat: 8AM - 6PM</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-border">
              {formSubmitted ? (
                <div className="text-center py-8" data-testid="form-success">
                  <div className="w-16 h-16 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#2E7D32]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1C1A] mb-2">Thank You!</h3>
                  <p className="text-[#52525B]">We have received your request and will contact you shortly.</p>
                  <Button 
                    onClick={() => setFormSubmitted(false)} 
                    className="btn-secondary mt-4"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1C1A] mb-1">Name *</label>
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="form-input-focus"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1C1A] mb-1">Phone *</label>
                      <Input
                        type="tel"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        required
                        className="form-input-focus"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1C1A] mb-1">Email *</label>
                    <Input
                      type="email"
                      placeholder="Your email address"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="form-input-focus"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1C1A] mb-1">Service Needed</label>
                    <select
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                      data-testid="select-service"
                    >
                      <option value="">Select a service</option>
                      <option value="general">General Pest Control</option>
                      <option value="termite">Termite Control</option>
                      <option value="rodent">Rodent Control</option>
                      <option value="bedbug">Bed Bug Treatment</option>
                      <option value="commercial">Commercial Services</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1C1A] mb-1">Message</label>
                    <Textarea
                      placeholder="Tell us about your pest problem..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={4}
                      className="form-input-focus"
                      data-testid="input-message"
                    />
                  </div>
                  <Button type="submit" className="btn-primary w-full" data-testid="submit-quote-btn">
                    Get Free Quote
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-12" data-testid="map-section">
        <div className="container-main">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d180776.75892508587!2d-93.4037!3d44.9706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b333909377bbbd%3A0x939fc9842f7aee07!2sMinneapolis%2C%20MN!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Primes Pick Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-gradient py-16 sm:py-20" data-testid="cta-section">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready for a Pest-Free Home?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Don&apos;t let pests take over your space. Contact us today for a free inspection 
            and take the first step towards a safer, healthier home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-[#2E7D32] hover:bg-white/90 rounded-full px-8 py-4 font-semibold shadow-lg" data-testid="cta-schedule-btn">
                Schedule Free Inspection
              </Button>
            </Link>
            <a href="tel:+18883568891" className="btn-accent" data-testid="cta-call-btn">
              <Phone className="w-5 h-5" />
              Call: +1-888-356-8891
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
